import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ListingLoading, ListingRestaurant } from '@pages/Listing/model/types'
import { listingInitialState } from '@pages/Listing/model/initialState'
import type { TypeMappingSelectionOutput as MappedSelection } from '@shared/api/middleware/mappingAPI/selection/type'
import type { TypeMappingPaginationOutput as MappedPagination } from '@shared/api/middleware/mappingAPI/pagination/type'
import type { PropsRestaurantListParams } from '@shared/api/types/apiPublicParameters'

const uniqueRestaurantsById = (restaurants: ListingRestaurant[]): ListingRestaurant[] => {
  const seen = new Set<number>()
  return restaurants.filter((restaurant) => {
    if (seen.has(restaurant.id)) return false
    seen.add(restaurant.id)
    return true
  })
}

const listing = createSlice({
  name: 'listing',
  initialState: listingInitialState,

  reducers: {
    setReady(state, action: PayloadAction<boolean>) {
      state.isReady = action.payload
    },
    setLoadingPage(state, action: PayloadAction<boolean>) {
      state.loading.page = action.payload
    },
    setLoading(state, action: PayloadAction<ListingLoading>) {
      state.loading = action.payload
    },

    setTitle(state, action: PayloadAction<string>) {
      state.title = action.payload
    },
    setDescription(state, action: PayloadAction<string>) {
      state.description = action.payload
    },

    setPagination(state, action: PayloadAction<MappedPagination | null>) {
      state.pagination = action.payload
    },
    setPaginationItems(state, action: PayloadAction<number | null>) {
      if (state.pagination) state.pagination.total = action.payload
    },
    setPaginationPage(state, action: PayloadAction<MappedPagination>) {
      if (state.pagination) state.pagination.page = action.payload.page
    },
    setList(state, action: PayloadAction<ListingRestaurant[]>) {
      state.list = uniqueRestaurantsById(action.payload)
    },
    appendListUnique(state, action: PayloadAction<ListingRestaurant[]>) {
      const list = state.list ?? []
      const existingIds = new Set(list.map((restaurant) => restaurant.id))
      const newRestaurants = action.payload.filter((restaurant) => {
        if (existingIds.has(restaurant.id)) return false
        existingIds.add(restaurant.id)
        return true
      })
      state.list = newRestaurants.length ? [...list, ...newRestaurants] : list
    },
    upsertRestaurantInList(state, action: PayloadAction<ListingRestaurant>) {
      if (!state.list) return
      const nextRest = action.payload
      const index = state.list.findIndex((rest) => rest.id === nextRest.id)
      if (index === -1) return
      state.list[index] = nextRest
    },

    setSelectionsSection(state, action: PayloadAction<MappedSelection[]>) {
      state.selections.list = action.payload
      state.selections.loading = false
    },

    setParamsRequest(state, action: PayloadAction<PropsRestaurantListParams | null>) {
      if (!action.payload) return
      const { page_size: _page_size, page: _page, city: _city, ...params } = action.payload
      state.paramsRequest = params
    },

    clearStateListing() {
      return listingInitialState
    },
  },
})

export const {
  setLoading,
  setTitle,
  setDescription,
  setPagination,
  setPaginationItems,
  setPaginationPage,
  setList,
  setSelectionsSection,
  setLoadingPage,
  clearStateListing,
  setParamsRequest,
  setReady,
  appendListUnique,
  upsertRestaurantInList,
} = listing.actions
export default listing.reducer
